import React, { useRef, useState } from 'react';
import { Field } from 'react-final-form';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import useNotification from 'effects/useNotification';
import { FormRecordButton, FormUploadButton } from '../../Button/FormButtons';
import { CGImageRef } from '../../../types/cardgift';
import debug from 'debug';
import FormHelperText from '@mui/material/FormHelperText';
import FormControl from '@mui/material/FormControl';
import Storage from '@aws-amplify/storage';
import { v4 as uuid } from 'uuid';
import LinearProgress from '@mui/material/LinearProgress';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import {
  MAX_VIDEO_SIZE_MB,
  MAX_IMAGE_SIZE_MB,
  VIDEO_SIZE_ERROR,
  IMAGE_SIZE_ERROR,
  VIDEO_FILE_TYPES,
  IMAGE_FILE_TYPES,
  MIN_IMAGE_WIDTH,
  MIN_IMAGE_HEIGHT,
  IMAGE_DIMENSION_ERROR,
} from 'utilities/constants';
import Stack from '@mui/material/Stack';
import FormStepDialog from 'components/CardConfigurer/steps/stepComponents/FormStepDialog';
import VideoCapture from 'components/VideoCapture';
import { serverErrorMessage } from 'utilities/constants';

const d = debug('UploadFieldToS3');

const useStyles = makeStyles(
  (theme: Theme) => ({
    text: {
      paddingTop: 8,
      textAlign: 'center',
    },
  }),
  { name: 'UploadFieldToS3' },
);

const UploadFieldToS3: React.FC<{
  name: string;
  type: 'image' | 'video';
  // values: any;
  helperText?: string | React.ReactNode;
  label?: string;
  accept?: string;
  defaultValue?: any;
  hideOnValid?: boolean;
  required?: boolean;
  allowRecordVideo?: boolean;
  mobileMode?: boolean;
}> = ({
  name, // props
  type = 'image',
  helperText = null,
  hideOnValid = false,
  label = 'Upload',
  accept = 'image/png,image/jpeg',
  defaultValue,
  required = false,
  allowRecordVideo = false,
  mobileMode = false,
}) => {
  const ref = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<number>();
  const [recordMode, setRecordMode] = useState(false);
  const classes = useStyles();

  const showMessage = useNotification();

  const handleUpload = () => {
    if (ref && ref.current) {
      ref.current.click();
    }
  };

  const handleProgress = (progress: any) => {
    d(`Uploaded: ${progress.loaded}/${progress.total}`);
    setProgress(Math.round((progress.loaded / progress.total) * 100));
  };

  const uploadFile = async (file: File | Blob, fileInfo: any = {}) => {
    d('upload', file, fileInfo);

    return Storage.put(uuid(), file, {
      level: 'public',
      contentType: file.type,
      progressCallback: handleProgress,
    })
      .then((x: any) => {
        d('here', x);
        return {
          key: x.key,
          ...fileInfo,
        };
      })
      .catch((e) => {
        d('err', e);
        throw e;
      });
  };

  const handleUploadFile = async (file: File, objectURL: string) => {
    d('upload validation', file);

    //video upload validation
    if (VIDEO_FILE_TYPES.includes(file.type) && file.size > MAX_VIDEO_SIZE_MB * 1000000) {
      throw Error(VIDEO_SIZE_ERROR);
    }
    //image upload validation
    if (IMAGE_FILE_TYPES.includes(file.type)) {
      if (file.size > MAX_IMAGE_SIZE_MB * 1000000) {
        throw Error(IMAGE_SIZE_ERROR);
      }
      return getImageDimensions(objectURL).then((res: CGImageRef) => {
        if (res.width < MIN_IMAGE_WIDTH || res.height < MIN_IMAGE_HEIGHT) {
          throw Error(IMAGE_DIMENSION_ERROR);
        }
        return uploadFile(file, {
          url: objectURL,
          width: res.width,
          height: res.height,
          aspectRatio: res.width / res.height,
        });
      });
    }

    return uploadFile(file, {
      url: objectURL,
    });
  };

  const handleUploadBlob = async (data: Blob) => {
    console.debug('theFile', data);
    return uploadFile(data, {
      url: URL.createObjectURL(data),
      type: data.type,
    });
  };

  const getImageDimensions = async (imageRef: string): Promise<CGImageRef> => {
    return new Promise((resolve, reject) => {
      var image = new Image();
      image.onload = () => {
        d('loaded', image);
        const details: CGImageRef = {
          url: imageRef,
          width: image.width,
          height: image.height,
          aspectRatio: image.width / image.height,
        };
        resolve(details);
      };
      image.src = imageRef;
    });
  };

  return (
    <>
      <Field
        name={name}
        defaultValue={defaultValue}
        validate={(value) => {
          d('validatingUploadField:' + name, { required, value });
          if (!required && !value) return undefined;
          if (required && (!value || !value.hasOwnProperty('key'))) return 'Upload required!';
          // if (!value || !value.key) return 'File uploading...';
          return undefined;
        }}
      >
        {({ input: { value, onChange }, meta: { valid, pristine } }) => (
          <>
            <FormControl
              variant="standard"
              sx={{
                flexGrow: 1,
                // hide the input when we have a value
                display: hideOnValid && value.key ? 'none' : 'flex',
                flexFlow: 'column',
              }}
            >
              <input
                onChange={(ev) => {
                  if (ev.target && ev.target.files) {
                    const file = ev.target.files[0];
                    if (file) {
                      d(file);
                      const objectURL = URL.createObjectURL(file);

                      if ((type === 'video' && VIDEO_FILE_TYPES.includes(file.type)) || (type === 'image' && IMAGE_FILE_TYPES.includes(file.type))) {
                        handleUploadFile(file, objectURL)
                          .then((done: any) => {
                            d('uploadDone', done);
                            onChange(done);
                          })
                          .catch((err) => {
                            d('uploadError', err);
                            showMessage(err.message, 'error');
                          })
                          .finally(() => {
                            setProgress(undefined);
                            if (ref && ref.current) {
                              ref.current.value = '';
                            }
                          });
                      } else {
                        showMessage(`Unsupported file type: ${file.type}. Please upload ${type === 'video' ? 'a video' : 'an image'}.`, 'error');
                        console.warn('Unsupported...', file.type);
                      }
                    }
                  }
                }}
                accept={accept}
                ref={ref}
                style={{ display: 'none' }}
                type="file"
              />
              <Collapse in={progress !== undefined && progress !== 100}>
                <Box mb={4}>
                  <LinearProgress color="secondary" variant="determinate" value={progress || 0} />
                  <FormHelperText>Uploading: {progress}%</FormHelperText>
                </Box>
              </Collapse>
              <Stack spacing={2} direction={{ xs: 'column', sm: 'row', md: 'column', lg: 'row' }}>
                {type === 'video' &&
                  !mobileMode && ( // We only show recording on desktop...
                    <FormRecordButton fullWidth disabled={progress !== undefined && progress !== 100} variant="outlined" color="primary" onClick={() => setRecordMode(true)}>
                      Record Video
                    </FormRecordButton>
                  )}
                <FormUploadButton fullWidth disabled={progress !== undefined && progress !== 100} variant="outlined" color="primary" onClick={handleUpload}>
                  {label}
                </FormUploadButton>
              </Stack>
              <FormHelperText className={classes.text}>{helperText}</FormHelperText>
            </FormControl>
            {recordMode && (
              <FormStepDialog open={recordMode} onClose={() => setRecordMode(false)}>
                <VideoCapture
                  onReceiveBlob={(data) => {
                    setRecordMode(false);
                    handleUploadBlob(data)
                      .then((done: any) => {
                        d('uploadDone', done);
                        onChange(done);
                      })
                      .catch((err) => {
                        d('uploadError', err);
                        showMessage(serverErrorMessage, 'error');
                      })
                      .finally(() => {
                        setProgress(undefined);
                        if (ref && ref.current) {
                          ref.current.value = '';
                        }
                      });
                  }}
                />
              </FormStepDialog>
            )}
          </>
        )}
      </Field>
    </>
  );
};

export default UploadFieldToS3;
